import { apiService } from "./api";
import toast from "react-hot-toast";

export const authService = {
  // Email/Password Authentication
  login: async (email, password) => {
    try {
      const response = await apiService.post("/users/login", {
        email,
        password,
      });

      // Store token
      localStorage.setItem("token", response.access_token);

      // Fetch user profile from /users/me/profile endpoint
      const profileResponse = await apiService.get("/users/me");
      localStorage.setItem("user", JSON.stringify(profileResponse));

      return {
        user: profileResponse,
        token: response.access_token,
        success: true,
      };
    } catch (error) {
      // Extract the exact error message from the backend
      let errorMessage = "Login failed";

      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  },

  // User Registration
  register: async (userData) => {
    try {
      const response = await apiService.post("/users/signup", userData);
      // Note: Registration doesn't return a token, user needs to verify email first
      toast.success(
        "Registration successful! Please check your email for verification.",
      );

      return {
        user: response,
        success: true,
        message: "Please check your email for verification.",
      };
    } catch (error) {
      console.error("Registration error details:", error.response?.data);
      const errorMessage =
        error.response?.data?.detail || "Registration failed";
      throw new Error(errorMessage);
    }
  },

  // Google OAuth
  loginWithGoogle: async () => {
    try {
      // Redirect to Google OAuth instead of popup for better reliability
      const baseURL = import.meta.env.VITE_API_BASE_URL;
      const redirectUrl = `${baseURL}/auth/login/google`;

      // Store the current page URL to redirect back after OAuth
      const currentUrl = window.location.href;
      localStorage.setItem("oauth_redirect_url", currentUrl);

      // Redirect to Google OAuth
      window.location.href = redirectUrl;

      // This will redirect the user, so we return a promise that never resolves
      // The actual resolution happens in the OAuth callback
      return new Promise(() => {});
    } catch (error) {
      throw new Error("Google OAuth failed: " + error.message);
    }
  },

  // Web3 Authentication
  getWeb3Nonce: async (address) => {
    try {
      // Primary: new endpoint supporting wallet_address param
      try {
        const response = await apiService.get(
          `/web3/nonce?wallet_address=${address}`,
        );
        return response;
      } catch (e) {
        // Fallback to existing endpoint
        const response = await apiService.get(`/api/nonce?address=${address}`);
        return response;
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to get nonce";
      throw new Error(errorMessage);
    }
  },

  authenticateWeb3: async (address, jwt) => {
    try {
      const response = await apiService.post("/auth/login", {
        address,
        jwt,
        email: " ", // placeholder
        firstName: "Anonymous",
        lastName: " ",
      });

      // Store token
      localStorage.setItem("token", response.access_token);

      // Fetch complete user profile from /api/web3/me endpoint
      const profileResponse = await apiService.get("/api/web3/me");
      localStorage.setItem("user", JSON.stringify(profileResponse));

      return {
        user: profileResponse,
        token: response.access_token,
        success: true,
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Web3 authentication failed";
      throw new Error(errorMessage);
    }
  },

  // Email Verification
  verifyEmail: async (token) => {
    try {
      // This endpoint redirects, so we need to handle it differently
      const response = await apiService.get(
        `/users/verify-email?token=${token}`,
      );
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Email verification failed";
      throw new Error(errorMessage);
    }
  },

  resendVerification: async (email) => {
    try {
      const response = await apiService.post("/users/resend-verification", {
        email,
      });
      toast.success("Verification email sent!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to resend verification";
      throw new Error(errorMessage);
    }
  },

  // Password Reset
  forgotPassword: async (email) => {
    try {
      const response = await apiService.post("/users/forgot-password", {
        email,
      });
      toast.success("Password reset email sent!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to send password reset";
      throw new Error(errorMessage);
    }
  },

  resetPassword: async (token, newPassword) => {
    try {
      const response = await apiService.post("/users/reset-password", {
        token,
        new_password: newPassword,
      });
      toast.success("Password reset successful!");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Password reset failed";
      throw new Error(errorMessage);
    }
  },

  // User Profile
  getCurrentUser: async () => {
    try {
      const response = await apiService.get("/users/me");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to get user profile";
      throw new Error(errorMessage);
    }
  },

  getUserProfile: async () => {
    try {
      const response = await apiService.get("/users/me");
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to get user profile";
      throw new Error(errorMessage);
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await apiService.put("/users/me", profileData);

      // Validate response
      if (!response || typeof response !== "object") {
        throw new Error("Invalid response format from server");
      }

      // Don't show toast here - let the component handle it
      return response;
    } catch (error) {
      console.error("AuthService: Profile update error:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to update profile";
      throw new Error(errorMessage);
    }
  },

  // Onboarding
  completeOnboarding: async (onboardingData) => {
    try {
      const response = await apiService.post(
        "/users/me/onboarding",
        onboardingData,
      );

      // After successful onboarding, fetch updated user profile
      const updatedUser = await authService.getUserProfile();

      // Update localStorage with the complete updated user data
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Failed to complete onboarding";
      throw new Error(errorMessage);
    }
  },

  // Logout
  logout: async () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return { success: true };
    } catch (error) {
      throw new Error("Logout failed");
    }
  },

  // Check authentication status
  checkAuth: async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }

      // Use getUserProfile to get complete user data including country
      const user = await authService.getUserProfile();

      // Update stored user data
      localStorage.setItem("user", JSON.stringify(user));

      return {
        user,
        token,
        success: true,
      };
    } catch (error) {
      // Clear invalid token
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      throw new Error("Not authenticated");
    }
  },

  // Handle OAuth callback
  handleOAuthCallback: async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get("access_token");
    const tokenType = urlParams.get("token_type");
    const userId = urlParams.get("user_id");
    const error = urlParams.get("error");

    if (error) {
      toast.error(`OAuth error: ${error}`);
      return { success: false, error };
    }

    if (accessToken && userId) {
      localStorage.setItem("token", accessToken);

      try {
        // Fetch user profile from /users/me/profile endpoint
        const profileResponse = await apiService.get("/users/me");

        // Store the complete user data
        localStorage.setItem("user", JSON.stringify(profileResponse));

        return {
          user: profileResponse,
          token: accessToken,
          success: true,
        };
      } catch (error) {
        console.error("Failed to fetch user profile after OAuth:", error);
        // Fallback to basic user data
        const basicUser = { user_id: userId };
        localStorage.setItem("user", JSON.stringify(basicUser));

        return {
          user: basicUser,
          token: accessToken,
          success: true,
        };
      }
    }

    return { success: false, error: "Invalid OAuth response" };
  },

  // Handle email verification callback
  handleEmailVerificationCallback: async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get("success");
    const accessToken = urlParams.get("access_token");
    const userId = urlParams.get("user_id");
    const message = urlParams.get("message");
    const error = urlParams.get("error");

    if (error) {
      toast.error(`Email verification error: ${error}`);
      return { success: false, error };
    }

    if (success === "true" && accessToken && userId) {
      localStorage.setItem("token", accessToken);

      try {
        // Fetch the complete user data from the backend using the profile endpoint
        const userResponse = await apiService.get("/users/me");
        const user = userResponse;

        // Store the complete user data
        localStorage.setItem("user", JSON.stringify(user));

        toast.success(message || "Email verified successfully!");
        return {
          user: user,
          token: accessToken,
          success: true,
          message,
        };
      } catch (error) {
        console.error(
          "Failed to fetch user data after email verification:",
          error,
        );
        // Fallback to basic user data
        const basicUser = { user_id: userId };
        localStorage.setItem("user", JSON.stringify(basicUser));

        // Don't show toast here since it's already shown in the success path
        return {
          user: basicUser,
          token: accessToken,
          success: true,
          message,
        };
      }
    }

    return { success: false, error: "Invalid email verification response" };
  },
};
